
import { defineComponent, onBeforeMount, reactive } from 'vue'
import useVuelidate from '@vuelidate/core'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Modal from '@/common/components/UI/Modal/index.vue'

// module components
import CalculationFilters from '@/modules/BillingModule/components/CalculationFilters/index.vue'
import CalculationEditModal from '@/modules/BillingModule/components/CalculationEditModal/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import {
    BillingGetCalculationsParamsT,
    BillingPreviewTranscriptParamsT,
    CalculationT,
} from '@/core/types/billing.types'
import { CompanyT } from '@/core/types/companies.types'

// realisations
import { billing } from '@/core/realisations/billing.realisation'
import { companies } from '@/core/realisations/companies.realisation'

// constants
import { BillingCalculationsColumns } from '@/core/constants/columns/BillingCalculations.columns'

// validations
import { required } from '@vuelidate/validators'

// composable
import useNotifications from '@/common/composable/useNotifications'
import { useFile } from '@/common/composable/useFile'

// icons
import iconRefresh from '@/assets/icons/refresh.svg'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'failed-fetch': FailedFetch,
        'the-button': TheButton,
        'the-select': TheSelect,
        'the-input': TheInput,
        'table-scroll-wrapper': TableScrollWrapper,
        'the-table': TheTable,
        //
        'calculation-filters': CalculationFilters,
        'calculation-edit-modal': CalculationEditModal,
        iconRefresh,
        Modal,
    },
    setup() {
        const notifications = useNotifications()
        const { saveFile } = useFile()

        const state = reactive(new DataState<CalculationT>({}))

        const edit_state = reactive({
            is_open: false,
            calculation_id: null as number | null,
            invoiceNumber: null as string | null,
        })

        const filters_state = reactive({
            limit: 50,
            //
            companyId: undefined as number | undefined,
            orderCreatedAt: undefined as string | undefined,
            invoiceNumber: undefined as string | undefined,
        })

        const preview_state = reactive({
            is_opened: false,
            //
            companyId: null as number | null,
            deliveredAtFrom: null as string | null,
            deliveredAtTo: null as string | null,
        })

        const data_state = reactive({
            companies: new DataState<CompanyT>({}),
        })

        const state_form$ = useVuelidate(
            {
                companyId: { required },
                deliveredAtFrom: { required },
                deliveredAtTo: { required },
            },
            preview_state
        )

        const loadCalculations = async (page: number) => {
            state.pagination.current_page = page

            try {
                state.is_loading = true

                const params = {
                    page,
                    companyId: filters_state.companyId,
                    orderCreatedAt: filters_state.orderCreatedAt,
                    invoiceNumber: filters_state.invoiceNumber,
                    limit: filters_state.limit,
                }

                const response = await billing.getCalculations(params)
                state.list = response.data
                state.pagination.total_pages = response.meta.last_page
                state.pagination.total_items = response.meta.total

                state.is_failed = false
                state.is_completed = true
            } catch (error) {
                state.failure.catchError(error)

                if (!state.failure.error_state.is_cancel) {
                    state.is_failed = true
                }
            } finally {
                if (!state.failure.error_state.is_cancel) {
                    state.is_loading = false
                }
            }
        }

        const onLimitChange = (limit: number) => {
            filters_state.limit = limit
            loadCalculations(state.pagination.current_page)
        }

        onBeforeMount(() => loadCalculations(1))

        const applyFilters = (params: BillingGetCalculationsParamsT) => {
            filters_state.invoiceNumber = params.invoiceNumber || undefined
            filters_state.companyId = params.companyId
            filters_state.orderCreatedAt =
                params.orderCreatedAt === '' ? undefined : params.orderCreatedAt

            loadCalculations(1)
        }

        const resetFilters = () => {
            filters_state.companyId = undefined
            filters_state.orderCreatedAt = undefined
            filters_state.invoiceNumber = undefined

            loadCalculations(1)
        }

        const closeTranscriptPreview = () => {
            preview_state.is_opened = false
        }

        const previewTranscript = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                state.is_loading = true

                const params = {
                    companyId: preview_state.companyId,
                    deliveredAtFrom: preview_state.deliveredAtFrom,
                    deliveredAtTo: preview_state.deliveredAtTo,
                }

                const response = await billing.previewTranscript(
                    params as BillingPreviewTranscriptParamsT
                )
                saveFile(response, 'Предварительный транскрипт')

                closeTranscriptPreview()
            } catch (error: any) {
                if (error.length) {
                    notifications.notifyError({
                        error,
                        title: `Ошибка при получении предварительного транскрипта`,
                    })
                } else {
                    notifications.notify({
                        title: 'У компании нет вычислений',
                        type: 'danger',
                    })
                }
            } finally {
                state.is_loading = false
            }
        }

        //

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({
                    page,
                    search,
                    statusId: 1,
                    isExtended: 0,
                })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({ page, statusId: 1, isExtended: 0 })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCompanies = (search_query: string) => {
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        //

        const onPreviewTranscript = () => {
            loadCompanies(1)
            preview_state.is_opened = true
        }

        const toggleEdit = (id?: number | null, invoiceNumber?: string | null) => {
            edit_state.is_open = !edit_state.is_open
            edit_state.calculation_id = edit_state.calculation_id ? null : id || null
            edit_state.invoiceNumber = invoiceNumber || null
        }

        const setCost = async (id: number) => {
            try {
                state.is_loading = true
                const response = await billing.setCost(id)

                notifications.notify({
                    title: response.message,
                    type: response.code === 400 ? 'danger' : 'success',
                })

                loadCalculations(1)
            } catch (error: any) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'Произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const onUpdated = () => {
            toggleEdit()
            loadCalculations(1)
        }

        return {
            BillingCalculationsColumns,
            state,
            filters_state,
            edit_state,
            preview_state,
            data_state,
            state_form$,
            //
            loadCalculations,
            applyFilters,
            resetFilters,
            onPreviewTranscript,
            previewTranscript,
            //
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFindedCompany,
            //
            closeTranscriptPreview,
            toggleEdit,
            onUpdated,
            onLimitChange,
            setCost,
        }
    },
})
